import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, ExternalLink, StickyNote } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "todo", label: "Da fare", dot: "bg-muted-foreground", active: "bg-muted-foreground/20 text-foreground border-muted-foreground/40" },
  { key: "doing", label: "In corso", dot: "bg-amber-500", active: "bg-amber-500/15 text-amber-500 border-amber-500/40" },
  { key: "done", label: "Pronta", dot: "bg-emerald-500", active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" },
];

export const WorkNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notes");
      setNotes(data || []);
    } catch (err) {
      console.error("Caricamento note non riuscito:", err);
      toast.error("Impossibile caricare le note");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() && !url.trim()) { toast.error("Inserisci almeno un nome o un link"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/notes", { name, url, status: "todo" });
      setNotes((n) => [data, ...n]);
      setName(""); setUrl("");
      toast.success("Nota aggiunta");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile aggiungere la nota");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (note, status) => {
    if (note.status === status) return;
    setNotes((list) => list.map((x) => (x.id === note.id ? { ...x, status } : x)));
    try {
      await api.put(`/admin/notes/${note.id}`, { name: note.name, url: note.url, status });
    } catch {
      toast.error("Impossibile aggiornare lo stato");
      load();
    }
  };

  const remove = async (id) => {
    setNotes((n) => n.filter((x) => x.id !== id));
    try { await api.delete(`/admin/notes/${id}`); toast.success("Nota eliminata"); }
    catch { toast.error("Impossibile eliminare la nota"); load(); }
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: notes.filter((n) => (n.status || "todo") === s.key).length }), {});

  return (
    <div className="space-y-5" data-testid="work-notes">
      <div className="flex items-center gap-2">
        <StickyNote className="h-5 w-5 text-accent" />
        <h3 className="font-serif text-lg font-bold">Blocco note — video in lavorazione</h3>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Segna qui i video su cui stai lavorando: un nome per ricordartene e il link al video originale.
        Imposta lo stato per vedere a colpo d'occhio a che punto sei, ed elimina la nota quando il discorso è pronto.
        Questi appunti restano privati (non compaiono sul sito).
      </p>

      <div className="flex flex-col sm:flex-row gap-2 p-4 border border-border rounded-sm bg-muted/30">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome / promemoria (es. Putin — flotta Pacifico)"
          className="rounded-sm sm:w-1/3"
          data-testid="note-name-input"
        />
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Link al video originale (https://...)"
          className="rounded-sm flex-1"
          data-testid="note-url-input"
        />
        <Button onClick={add} disabled={saving} className="rounded-sm gap-1.5 shrink-0" data-testid="note-add-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Aggiungi
        </Button>
      </div>

      {!loading && notes.length > 0 && (
        <div className="flex gap-3 text-xs text-muted-foreground">
          {STATUSES.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}: {counts[s.key]}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-sm">
          Nessun video in lavorazione. Aggiungi il primo qui sopra.
        </p>
      ) : (
        <div className="border border-border rounded-sm divide-y divide-border" data-testid="notes-list">
          {notes.map((n) => {
            const st = n.status || "todo";
            const dot = STATUSES.find((s) => s.key === st)?.dot || "bg-muted-foreground";
            return (
              <div key={n.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3" data-testid={`note-${n.id}`}>
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{n.name || "(senza nome)"}</p>
                  {n.url && (
                    <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 truncate max-w-full" data-testid={`note-link-${n.id}`}>
                      <ExternalLink className="h-3 w-3 shrink-0" /> <span className="truncate">{n.url}</span>
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setStatus(n, s.key)}
                      className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${st === s.key ? s.active : "border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`note-status-${s.key}-${n.id}`}
                    >
                      {s.label}
                    </button>
                  ))}
                  <Button variant="ghost" size="icon" onClick={() => remove(n.id)} className="rounded-sm text-accent hover:text-accent shrink-0" data-testid={`note-delete-${n.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
